import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Trash2, Tag, Calendar, Percent, DollarSign, Users, Copy, ChevronLeft, ChevronRight, Check, X, Truck } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useBodyLock } from '../../hooks/useBodyLock';

interface Promocao {
  id: number;
  nome: string;
  descricao: string;
  tipo: 'percentual' | 'valor_fixo' | 'frete_gratis';
  valor: number;
  codigo: string;
  data_inicio: string;
  data_fim: string;
  uso_maximo: number;
  uso_atual: number;
  valor_minimo: number;
  ativo: boolean;
  aplicavel_em: 'todos' | 'categorias' | 'produtos';
  itens_aplicaveis: string[];
}

interface PromocaoForm {
  nome: string;
  descricao: string;
  tipo: 'percentual' | 'valor_fixo' | 'frete_gratis';
  valor: number;
  codigo: string;
  data_inicio: string;
  data_fim: string;
  uso_maximo: number;
  valor_minimo: number;
  aplicavel_em: 'todos' | 'categorias' | 'produtos';
  itens_aplicaveis: string[] | string;
}

const PromocoesPage: React.FC = () => {
  const [promocoes, setPromocoes] = useState<Promocao[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromocao, setEditingPromocao] = useState<Promocao | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;
  const [freteGratisPromocao, setFreteGratisPromocao] = useState<Promocao | null>(null);
  const [editandoFreteGratis, setEditandoFreteGratis] = useState(false);
  const [valorMinimoFreteGratis, setValorMinimoFreteGratis] = useState(50);

  // Bloquear scroll do body quando modal aberto no mobile
  useBodyLock(showModal);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { errors }
  } = useForm<PromocaoForm>();

  const tipo = watch('tipo') as 'percentual' | 'valor_fixo' | 'frete_gratis' | undefined;
  const aplicavelEm = watch('aplicavel_em');

  useEffect(() => {
    fetchPromocoes();
  }, []);

  const fetchPromocoes = async () => {
    try {
      const response = await api.get('/api/promocoes');
      const todasPromocoes = response.data || [];
      
      // Filtrar promoções com ID válido (maior que 0 e não null/undefined)
      const promocoesValidas = todasPromocoes.filter((p: Promocao) => 
        p.id && typeof p.id === 'number' && p.id > 0 && Number.isInteger(p.id)
      );
      
      // Separar promoção de frete grátis das outras
      const freteGratis = promocoesValidas.find((p: Promocao) => p.tipo === 'frete_gratis');
      const outrasPromocoes = promocoesValidas.filter((p: Promocao) => p.tipo !== 'frete_gratis');
      
      setFreteGratisPromocao(freteGratis || null);
      setPromocoes(outrasPromocoes);
      
      if (freteGratis) {
        setValorMinimoFreteGratis(freteGratis.valor_minimo || 50);
      }
      
      // Log de promoções inválidas encontradas (apenas em desenvolvimento)
      const promocoesInvalidas = todasPromocoes.filter((p: Promocao) => 
        !p.id || typeof p.id !== 'number' || p.id <= 0 || !Number.isInteger(p.id)
      );
      if (promocoesInvalidas.length > 0) {
        console.warn(`⚠️ Encontradas ${promocoesInvalidas.length} promoção(ões) com ID inválido:`, promocoesInvalidas);
      }
    } catch (error) {
      toast.error('Erro ao carregar promoções', { duration: 2000 });
    } finally {
      setLoading(false);
    }
  };

  // Validar passo atual
  const validateStep = (step: number, data: PromocaoForm): boolean => {
    switch (step) {
      case 1:
        // Para frete_gratis, código não é obrigatório
        if (data.tipo === 'frete_gratis') {
          if (!data.nome?.trim() || !data.descricao?.trim()) {
            toast.error('Preencha todos os campos obrigatórios do passo 1', { duration: 2000 });
            return false;
          }
        } else {
          if (!data.nome?.trim() || !data.codigo?.trim() || !data.descricao?.trim()) {
            toast.error('Preencha todos os campos obrigatórios do passo 1', { duration: 2000 });
            return false;
          }
        }
        return true;
      case 2:
        if (data.tipo === 'frete_gratis') {
          // Para frete_gratis, só precisa do valor_minimo
          if (data.valor_minimo === undefined || data.valor_minimo < 0) {
            toast.error('O valor mínimo deve ser maior ou igual a zero', { duration: 2000 });
            return false;
          }
        } else {
          if (data.valor === undefined || data.valor_minimo === undefined || !data.tipo) {
            toast.error('Preencha todos os campos obrigatórios do passo 2', { duration: 2000 });
            return false;
          }
          if (data.valor < 0) {
            toast.error('O valor do desconto deve ser maior ou igual a zero', { duration: 2000 });
            return false;
          }
          if (data.tipo === 'percentual' && data.valor > 100) {
            toast.error('O percentual não pode ser maior que 100%', { duration: 2000 });
            return false;
          }
        }
        return true;
      case 3:
        if (!data.data_inicio || !data.data_fim || !data.uso_maximo || !data.aplicavel_em) {
          toast.error('Preencha todos os campos obrigatórios do passo 3', { duration: 2000 });
          return false;
        }
        if (new Date(data.data_inicio) > new Date(data.data_fim)) {
          toast.error('A data de início deve ser anterior à data de fim', { duration: 2000 });
          return false;
        }
        return true;
      default:
        return false;
    }
  };

  // Navegar para próximo passo
  const handleNextStep = (data: PromocaoForm) => {
    if (validateStep(currentStep, data)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        // Último passo - salvar promoção
        handleFinalSubmit(data);
      }
    }
  };

  // Voltar passo anterior
  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Salvar promoção final
  const handleFinalSubmit = async (data: PromocaoForm) => {
    try {
      // Para frete_gratis, usar valores padrão
      if (data.tipo === 'frete_gratis') {
        data.codigo = data.codigo || 'FRETEGRATIS';
        data.valor = 0; // Não usado para frete grátis
        data.aplicavel_em = 'todos';
        data.itens_aplicaveis = [];
      }

      // Processar itens_aplicaveis se for string
      let itensAplicaveis: string[] = [];
      if (data.itens_aplicaveis) {
        if (typeof data.itens_aplicaveis === 'string') {
          itensAplicaveis = data.itens_aplicaveis.split(',').map((item: string) => item.trim()).filter((item: string) => item.length > 0);
        } else if (Array.isArray(data.itens_aplicaveis)) {
          itensAplicaveis = data.itens_aplicaveis.filter((item): item is string => typeof item === 'string' && item.length > 0);
        }
      }

      const dataToSubmit = {
        ...data,
        itens_aplicaveis: itensAplicaveis
      };

      if (editingPromocao && editingPromocao.id && editingPromocao.id > 0) {
        await api.put(`/api/promocoes/${editingPromocao.id}`, dataToSubmit);
        toast.success('Promoção atualizada com sucesso!', { duration: 1500 });
      } else {
        await api.post('/api/promocoes', dataToSubmit);
        toast.success('Promoção criada com sucesso!', { duration: 1500 });
      }
      setShowModal(false);
      setEditingPromocao(null);
      setCurrentStep(1);
      reset();
      fetchPromocoes();
    } catch (error) {
      toast.error('Erro ao salvar promoção', { duration: 2000 });
    }
  };

  // Salvar configuração de frete grátis
  const salvarFreteGratis = async () => {
    if (valorMinimoFreteGratis < 0) {
      toast.error('O valor mínimo deve ser maior ou igual a zero', { duration: 2000 });
      return;
    }

    try {
      const dataPromocao = {
        nome: 'Frete Grátis',
        descricao: `Frete grátis para pedidos acima de R$ ${valorMinimoFreteGratis.toFixed(2)}`,
        tipo: 'frete_gratis',
        valor: 0,
        codigo: 'FRETEGRATIS',
        data_inicio: new Date().toISOString().split('T')[0],
        data_fim: new Date(new Date().setFullYear(new Date().getFullYear() + 10)).toISOString().split('T')[0], // 10 anos no futuro
        uso_maximo: 999999,
        valor_minimo: valorMinimoFreteGratis,
        aplicavel_em: 'todos',
        itens_aplicaveis: [],
        ativo: true
      };

      if (freteGratisPromocao && freteGratisPromocao.id && freteGratisPromocao.id > 0) {
        // Atualizar promoção existente
        await api.put(`/api/promocoes/${freteGratisPromocao.id}`, {
          ...dataPromocao,
          ativo: freteGratisPromocao.ativo
        });
        toast.success('Configuração de frete grátis atualizada!', { duration: 1500 });
      } else {
        // Criar nova promoção
        await api.post('/api/promocoes', dataPromocao);
        toast.success('Configuração de frete grátis criada!', { duration: 1500 });
      }
      
      setEditandoFreteGratis(false);
      fetchPromocoes();
    } catch (error: any) {
      console.error('Erro ao salvar frete grátis:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar configuração de frete grátis', { duration: 2000 });
    }
  };

  const onSubmit = (data: PromocaoForm) => {
    handleNextStep(data);
  };

  const handleEdit = (promocao: Promocao) => {
    setEditingPromocao(promocao);
    setCurrentStep(1);
    reset({
      nome: promocao.nome,
      descricao: promocao.descricao,
      tipo: promocao.tipo,
      valor: promocao.valor,
      codigo: promocao.codigo,
      data_inicio: promocao.data_inicio.split('T')[0],
      data_fim: promocao.data_fim.split('T')[0],
      uso_maximo: promocao.uso_maximo,
      valor_minimo: promocao.valor_minimo,
      aplicavel_em: promocao.aplicavel_em,
      itens_aplicaveis: Array.isArray(promocao.itens_aplicaveis) 
        ? promocao.itens_aplicaveis.join(', ')
        : promocao.itens_aplicaveis || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    // Converter para número se necessário e validar
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (!numericId || isNaN(numericId) || numericId <= 0 || !Number.isInteger(numericId)) {
      console.error('ID inválido recebido:', id, 'Tipo:', typeof id);
      toast.error('ID da promoção inválido', { duration: 2000 });
      return;
    }
    if (!window.confirm('Tem certeza que deseja excluir esta promoção?')) {
      return;
    }

    try {
      await api.delete(`/api/promocoes/${numericId}`);
      toast.success('Promoção excluída com sucesso!', { duration: 1500 });
      fetchPromocoes();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.mensagem || error?.message || 'Erro ao excluir promoção';
      console.error('Erro ao excluir promoção:', error);
      toast.error(errorMessage, { duration: 2000 });
    }
  };

  const toggleStatus = async (id: number, ativo: boolean) => {
    // Converter para número se necessário e validar
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (!numericId || isNaN(numericId) || numericId <= 0 || !Number.isInteger(numericId)) {
      console.error('ID inválido recebido:', id, 'Tipo:', typeof id);
      toast.error('ID da promoção inválido', { duration: 2000 });
      return;
    }
    try {
      await api.put(`/api/promocoes/${numericId}`, { ativo: !ativo });
      toast.success(`Promoção ${ativo ? 'desativada' : 'ativada'} com sucesso!`, { duration: 1500 });
      fetchPromocoes();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.mensagem || error?.message || 'Erro ao alterar status da promoção';
      console.error('Erro ao alterar status:', error);
      toast.error(errorMessage, { duration: 2000 });
    }
  };

  const copyCode = (codigo: string) => {
    navigator.clipboard.writeText(codigo);
    toast.success('Código copiado para a área de transferência!', { duration: 1500 });
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const isExpired = (dataFim: string) => {
    return new Date(dataFim) < new Date();
  };

  const isActive = (promocao: Promocao) => {
    const now = new Date();
    const inicio = new Date(promocao.data_inicio);
    const fim = new Date(promocao.data_fim);
    return promocao.ativo && now >= inicio && now <= fim && promocao.uso_atual < promocao.uso_maximo;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 md:h-64 lg:h-80">
        <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4 lg:space-y-6 px-2 md:px-4 lg:px-0">
      {/* Header - Mobile: Stack, iPad/Desktop: Row */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-4">
        <div>
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
            Promoções e Cupons
          </h1>
          <p className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-400 mt-1">
            Gerencie promoções e cupons de desconto
          </p>
        </div>
        <button
          onClick={() => {
            setEditingPromocao(null);
            setCurrentStep(1);
            reset();
            setShowModal(true);
          }}
          className="btn-primary flex items-center justify-center gap-2 w-full md:w-auto text-sm md:text-base px-4 py-2 md:py-2.5"
        >
          <Plus className="h-4 w-4 md:h-5 md:w-5" />
          <span className="hidden md:inline">Nova Promoção</span>
          <span className="md:hidden">Nova</span>
        </button>
      </div>

      {/* Seção de Configuração de Frete Grátis */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6 border-2 border-green-200 dark:border-green-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Truck className="h-5 w-5 md:h-6 md:w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h2 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">
                Frete Grátis
              </h2>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                Configure o valor mínimo para entrega grátis
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {freteGratisPromocao && (
              <span className={`px-3 py-1 rounded-full text-xs md:text-sm font-medium ${
                freteGratisPromocao.ativo 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
              }`}>
                {freteGratisPromocao.ativo ? 'Ativo' : 'Inativo'}
              </span>
            )}
            {freteGratisPromocao && freteGratisPromocao.id && freteGratisPromocao.id > 0 && (
              <button
                onClick={() => toggleStatus(freteGratisPromocao.id!, freteGratisPromocao.ativo)}
                className={`px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors ${
                  freteGratisPromocao.ativo
                    ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-200 dark:hover:bg-yellow-800'
                    : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800'
                }`}
              >
                {freteGratisPromocao.ativo ? 'Desativar' : 'Ativar'}
              </button>
            )}
          </div>
        </div>

        {editandoFreteGratis ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm md:text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                Valor Mínimo do Pedido (R$) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={valorMinimoFreteGratis}
                onChange={(e) => setValorMinimoFreteGratis(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="50.00"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Pedidos acima deste valor terão frete grátis automaticamente
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={salvarFreteGratis}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors text-sm md:text-base font-medium"
              >
                Salvar
              </button>
              <button
                onClick={() => {
                  setEditandoFreteGratis(false);
                  if (freteGratisPromocao) {
                    setValorMinimoFreteGratis(freteGratisPromocao.valor_minimo || 50);
                  }
                }}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors text-sm md:text-base font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
                Valor mínimo atual:
              </p>
              <p className="text-2xl md:text-3xl font-bold text-green-600 dark:text-green-400">
                R$ {valorMinimoFreteGratis.toFixed(2)}
              </p>
              {freteGratisPromocao && (
                <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {freteGratisPromocao.descricao}
                </p>
              )}
            </div>
            <button
              onClick={() => setEditandoFreteGratis(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm md:text-base font-medium flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              {freteGratisPromocao ? 'Editar' : 'Configurar'}
            </button>
          </div>
        )}
      </div>

      {/* Promoções Grid - Mobile: 1 col, iPad: 2 cols, Desktop: 3 cols */}
      {promocoes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
        {promocoes.filter((p) => p.id && p.id > 0).map((promocao) => (
          <div
            key={promocao.id}
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border-2 transition-transform hover:scale-[1.02] md:hover:scale-[1.01] ${
              isActive(promocao) ? 'border-green-200 dark:border-green-800' : 
              isExpired(promocao.data_fim) ? 'border-red-200 dark:border-red-800' : 
              'border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="p-3 md:p-4">
              <div className="flex justify-between items-start mb-2 md:mb-3 gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white truncate">
                    {promocao.nome}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                    {promocao.descricao}
                  </p>
                </div>
                <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleStatus(promocao.id, promocao.ativo)}
                    className={`px-2 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium whitespace-nowrap ${
                      promocao.ativo 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {promocao.ativo ? 'Ativa' : 'Inativa'}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5 md:space-y-2 mb-3 md:mb-4">
                <div className="flex justify-between items-center gap-2">
                  <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Código:</span>
                  <div className="flex items-center gap-1 md:gap-2">
                    <span className="font-mono text-xs md:text-sm font-medium text-gray-900 dark:text-white truncate max-w-[100px] md:max-w-none">
                      {promocao.codigo}
                    </span>
                    <button
                      onClick={() => copyCode(promocao.codigo)}
                      className="p-0.5 md:p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                      title="Copiar código"
                    >
                      <Copy className="h-3 w-3 md:h-3.5 md:w-3.5" />
                    </button>
                  </div>
                </div>

                {promocao.tipo !== 'frete_gratis' && (
                  <div className="flex justify-between text-xs md:text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Desconto:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {promocao.tipo === 'percentual' 
                        ? `${Number(promocao.valor || 0)}%` 
                        : `R$ ${Number(promocao.valor || 0).toFixed(2)}`}
                    </span>
                  </div>
                )}

                <div className="flex justify-between text-xs md:text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Valor Mínimo:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    R$ {Number(promocao.valor_minimo || 0).toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between text-xs md:text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Usos:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {promocao.uso_atual}/{promocao.uso_maximo}
                  </span>
                </div>

                <div className="flex justify-between text-xs md:text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Válida até:</span>
                  <span className="font-medium text-gray-900 dark:text-white text-right">
                    {new Date(promocao.data_fim).toLocaleDateString('pt-BR')}
                  </span>
                </div>

                <div className="flex justify-between text-xs md:text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Aplicável em:</span>
                  <span className="font-medium text-gray-900 dark:text-white text-right">
                    {promocao.aplicavel_em === 'todos' ? 'Todos' : 
                     promocao.aplicavel_em === 'categorias' ? 'Categorias' : 'Produtos'}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2">
                <div className="flex gap-1 md:gap-2">
                  <button
                    onClick={() => handleEdit(promocao)}
                    className="p-1.5 md:p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                    title="Editar"
                  >
                    <Edit className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(promocao.id)}
                    className="p-1.5 md:p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    title="Excluir"
                  >
                    <Trash2 className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  </button>
                </div>
                <div className="text-right flex-shrink-0">
                  {isActive(promocao) && (
                    <span className="inline-flex items-center px-2 py-0.5 md:px-2.5 md:py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 whitespace-nowrap">
                      Válida
                    </span>
                  )}
                  {isExpired(promocao.data_fim) && (
                    <span className="inline-flex items-center px-2 py-0.5 md:px-2.5 md:py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 whitespace-nowrap">
                      Expirada
                    </span>
                  )}
                  {!isActive(promocao) && !isExpired(promocao.data_fim) && (
                    <span className="inline-flex items-center px-2 py-0.5 md:px-2.5 md:py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 whitespace-nowrap">
                      Inativa
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        </div>
      ) : (
        <div className="text-center py-8 md:py-12 lg:py-16">
          <div className="text-gray-500 dark:text-gray-400 mb-4 md:mb-6">
            <p className="text-base md:text-lg lg:text-xl font-medium mb-2">Nenhuma promoção cadastrada</p>
            <p className="text-xs md:text-sm lg:text-base">Comece criando sua primeira promoção!</p>
          </div>
          <button
            onClick={() => {
              setEditingPromocao(null);
              setCurrentStep(1);
              reset();
              setShowModal(true);
            }}
            className="btn-primary flex items-center gap-2 mx-auto text-sm md:text-base px-4 py-2 md:py-2.5"
          >
            <Plus className="h-4 w-4 md:h-5 md:w-5" />
            Criar Primeira Promoção
          </button>
        </div>
      )}

      {/* Modal - Passo a Passo */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 md:p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md md:max-w-lg lg:max-w-2xl max-h-[95vh] md:max-h-[90vh] overflow-y-auto">
            <div className="p-4 md:p-5 lg:p-6">
              {/* Header com título e botão fechar */}
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                  {editingPromocao ? 'Editar Promoção' : 'Nova Promoção'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingPromocao(null);
                    setCurrentStep(1);
                    reset();
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1"
                >
                  <X className="h-5 w-5 md:h-6 md:w-6" />
                </button>
              </div>

              {/* Indicador de Progresso */}
              <div className="mb-6 md:mb-8">
                <div className="flex items-center justify-between mb-2">
                  {[1, 2, 3].map((step) => (
                    <React.Fragment key={step}>
                      <div className="flex items-center">
                        <div
                          className={`flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full border-2 transition-colors ${
                            currentStep >= step
                              ? 'bg-blue-600 border-blue-600 text-white'
                              : 'bg-transparent border-gray-300 dark:border-gray-600 text-gray-400'
                          }`}
                        >
                          {currentStep > step ? (
                            <Check className="h-4 w-4 md:h-5 md:w-5" />
                          ) : (
                            <span className="text-xs md:text-sm font-semibold">{step}</span>
                          )}
                        </div>
                        <span
                          className={`ml-2 text-xs md:text-sm font-medium hidden md:block ${
                            currentStep >= step
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-gray-400'
                          }`}
                        >
                          {step === 1 && 'Informações Básicas'}
                          {step === 2 && 'Desconto e Valores'}
                          {step === 3 && 'Configurações'}
                        </span>
                      </div>
                      {step < totalSteps && (
                        <div
                          className={`flex-1 h-0.5 mx-2 md:mx-4 ${
                            currentStep > step ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div className="text-center mt-2">
                  <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    Passo {currentStep} de {totalSteps}
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 md:space-y-4">
                {/* Passo 1: Informações Básicas */}
                {currentStep === 1 && (
                  <div className="space-y-3 md:space-y-4">
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nome da Promoção <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...register('nome', { required: 'Nome é obrigatório' })}
                        type="text"
                        className="input-field text-sm md:text-base"
                        placeholder="Nome da promoção"
                      />
                      {errors.nome && (
                        <p className="mt-1 text-xs md:text-sm text-red-600">{errors.nome.message}</p>
                      )}
                    </div>

                    {tipo && tipo !== 'frete_gratis' && (
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Código <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                          <input
                            {...register('codigo', { required: 'Código é obrigatório' })}
                            type="text"
                            className="input-field flex-1 text-sm md:text-base"
                            placeholder="CÓDIGO123"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const code = generateCode();
                              const currentValue = getValues('codigo');
                              reset({ ...getValues(), codigo: code });
                            }}
                            className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-xs md:text-sm whitespace-nowrap"
                          >
                            Gerar
                          </button>
                        </div>
                        {errors.codigo && (
                          <p className="mt-1 text-xs md:text-sm text-red-600">{errors.codigo.message}</p>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Descrição <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        {...register('descricao', { required: 'Descrição é obrigatória' })}
                        rows={3}
                        className="input-field text-sm md:text-base"
                        placeholder="Descrição da promoção"
                      />
                      {errors.descricao && (
                        <p className="mt-1 text-xs md:text-sm text-red-600">{errors.descricao.message}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Passo 2: Desconto e Valores */}
                {currentStep === 2 && (
                  <div className="space-y-3 md:space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Tipo de Promoção <span className="text-red-500">*</span>
                        </label>
                        <select
                          {...register('tipo', { required: 'Tipo é obrigatório' })}
                          className="input-field text-sm md:text-base"
                        >
                          <option value="percentual">Percentual (%)</option>
                          <option value="valor_fixo">Valor Fixo (R$)</option>
                          <option value="frete_gratis">Frete Grátis</option>
                        </select>
                        {errors.tipo && (
                          <p className="mt-1 text-xs md:text-sm text-red-600">{errors.tipo.message}</p>
                        )}
                      </div>

                      {tipo && tipo !== 'frete_gratis' && (
                        <div>
                          <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Valor do Desconto <span className="text-red-500">*</span>
                          </label>
                          <input
                            {...register('valor', { 
                              required: 'Valor é obrigatório',
                              min: { value: 0, message: 'Valor deve ser maior ou igual a 0' },
                              max: { value: tipo && tipo === 'percentual' ? 100 : 999999, message: tipo && tipo === 'percentual' ? 'Percentual deve ser menor ou igual a 100%' : 'Valor muito alto' }
                            })}
                            type="number"
                            step={tipo && tipo === 'percentual' ? '1' : '0.01'}
                            className="input-field text-sm md:text-base"
                            placeholder={tipo && tipo === 'percentual' ? '10' : '5.00'}
                          />
                          {errors.valor && (
                            <p className="mt-1 text-xs md:text-sm text-red-600">{errors.valor.message}</p>
                          )}
                        </div>
                      )}
                      
                      {tipo === 'frete_gratis' && (
                        <div>
                          <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            <Truck className="h-4 w-4 inline mr-1" />
                            Frete Grátis Automático
                          </label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Esta promoção aplica frete grátis automaticamente quando o valor mínimo for atingido
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Valor Mínimo do Pedido <span className="text-red-500">*</span>
                        {tipo && tipo === 'frete_gratis' && (
                          <span className="ml-2 text-xs text-green-600 dark:text-green-400">
                            (Para frete grátis)
                          </span>
                        )}
                      </label>
                      <input
                        {...register('valor_minimo', { 
                          required: 'Valor mínimo é obrigatório',
                          min: { value: 0, message: 'Valor mínimo deve ser maior ou igual a 0' }
                        })}
                        type="number"
                        step="0.01"
                        className="input-field text-sm md:text-base"
                        placeholder={tipo && tipo === 'frete_gratis' ? '50.00' : '0.00'}
                      />
                      {errors.valor_minimo && (
                        <p className="mt-1 text-xs md:text-sm text-red-600">{errors.valor_minimo.message}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {tipo && tipo === 'frete_gratis' 
                          ? 'Pedidos acima deste valor terão frete grátis automaticamente'
                          : 'Valor mínimo do pedido para aplicar o desconto'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Passo 3: Configurações */}
                {currentStep === 3 && (
                  <div className="space-y-3 md:space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Data de Início <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register('data_inicio', { required: 'Data de início é obrigatória' })}
                          type="date"
                          className="input-field text-sm md:text-base"
                        />
                        {errors.data_inicio && (
                          <p className="mt-1 text-xs md:text-sm text-red-600">{errors.data_inicio.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Data de Fim <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register('data_fim', { required: 'Data de fim é obrigatória' })}
                          type="date"
                          className="input-field text-sm md:text-base"
                        />
                        {errors.data_fim && (
                          <p className="mt-1 text-xs md:text-sm text-red-600">{errors.data_fim.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Uso Máximo <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register('uso_maximo', { 
                            required: 'Uso máximo é obrigatório',
                            min: { value: 1, message: 'Uso máximo deve ser maior que 0' }
                          })}
                          type="number"
                          className="input-field text-sm md:text-base"
                          placeholder="100"
                        />
                        {errors.uso_maximo && (
                          <p className="mt-1 text-xs md:text-sm text-red-600">{errors.uso_maximo.message}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Número máximo de vezes que o cupom pode ser usado
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Aplicável em <span className="text-red-500">*</span>
                        </label>
                        <select
                          {...register('aplicavel_em', { required: 'Campo obrigatório' })}
                          className="input-field text-sm md:text-base"
                        >
                          <option value="todos">Todos os produtos</option>
                          <option value="categorias">Categorias específicas</option>
                          <option value="produtos">Produtos específicos</option>
                        </select>
                        {errors.aplicavel_em && (
                          <p className="mt-1 text-xs md:text-sm text-red-600">{errors.aplicavel_em.message}</p>
                        )}
                      </div>
                    </div>

                    {(aplicavelEm === 'categorias' || aplicavelEm === 'produtos') && (
                      <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {aplicavelEm === 'categorias' ? 'Categorias' : 'Produtos'} Aplicáveis
                        </label>
                        <textarea
                          {...register('itens_aplicaveis')}
                          rows={3}
                          className="input-field text-sm md:text-base"
                          placeholder={aplicavelEm === 'categorias' ? 'Digite as categorias separadas por vírgula (ex: Bebidas, Lanches)' : 'Digite os produtos separados por vírgula (ex: Pizza Grande, Hambúrguer)'}
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Separe os itens por vírgula
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Botões de Navegação */}
                <div className="flex flex-col md:flex-row gap-2 md:gap-3 pt-4 md:pt-6 border-t border-gray-200 dark:border-gray-700">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="flex items-center justify-center gap-2 btn-secondary text-sm md:text-base py-2 md:py-2.5 order-2 md:order-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Anterior
                    </button>
                  )}
                  <button
                    type="submit"
                    className={`flex items-center justify-center gap-2 btn-primary text-sm md:text-base py-2 md:py-2.5 ${
                      currentStep === 1 ? 'w-full' : 'flex-1'
                    } order-1 md:order-2`}
                  >
                    {currentStep < totalSteps ? (
                      <>
                        Próximo
                        <ChevronRight className="h-4 w-4" />
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4" />
                        {editingPromocao ? 'Atualizar Promoção' : 'Finalizar e Criar'}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingPromocao(null);
                      setCurrentStep(1);
                      reset();
                    }}
                    className="btn-secondary text-sm md:text-base py-2 md:py-2.5 order-3"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromocoesPage;
